// Counter Parts — renders the paired item(s) declared via keyValues[counterpart]
// (e.g. mirror left / right) for the current product, as a grid of product cards
// with quick-view drawer + add-to-cart.
//
// Config (read from the TARGET element, not the script tag):
//   #parts-counter-parts  OR  [data-parts-counter-parts]  — the container
//   data-sku-selector="#product-sku"  (optional; overrides the SKU source)
//   data-limit="6"                    (optional; how many counter parts to fetch)
//   data-title="Counter parts"        (optional; section heading)
//
// SKU source (first hit wins): the container's data-sku, then data-sku-selector /
// #product-sku, then any [data-sku] on the page.
//
// When no explicit container exists on a product page we graft a "Counter Parts"
// tab into the storefront's existing .nav-tabs-contain (ensureTabContainer).

import { getAppBaseUrl, inAppPartUrl } from '../shared/config.js';
import { addedToCartToast } from '../shared/cartToast.js';
import { getTopDocument, getTopBody, getSku, esc } from '../shared/dom.js';
import { getSessionRef, loadFeatures, isAnonymous } from '../shared/identity.js';
import { apiFetch } from '../shared/api.js';
import { formatPriceIncl, formatPriceExcl, workshopLineHtml } from '../shared/format.js';

// Overrides written by fetchPricesAndStock — match how ResultsGrid reads
// from priceStore.prices / priceStore.stockBysku on the React side.
var _priceOverrides = {};   // { sku: number }  (excl-VAT)
var _stockOverrides = {};   // { sku: { available: number, status: 'in_stock'|'out_of_stock' } }
var _reducedFromOverrides = {}; // { sku: number } (excl-VAT "was" price for promos)
var _standardPriceOverrides = {}; // { sku: number } (excl-VAT Default/B2C/retail price)
// Role-based UI feature flags resolved from the storefront's commerce_features
// JWT. Populated once on init; subsequent card renders read this object.
var _features = {};
// The resolved target element — used by the bfcache pageshow reset below.
var _container = null;

function _loginUrl() {
	try {
		var origin = (window.top && window.top.location && window.top.location.origin) || window.location.origin;
		var loc = (window.top && window.top.location) || window.location;
		// redirectTo must be a relative PATH (path + query), not the absolute URL.
		return origin + '/account/login?redirectTo=' + encodeURIComponent(loc.pathname + loc.search + loc.hash);
	} catch (e) {
		return '/account/login';
	}
}

function _loginPromptHtml() {
	return ''
		+ '<a href="' + esc(_loginUrl()) + '" class="pcp-login-for-pricing" '
		+ 'style="display:flex;align-items:center;gap:6px;padding:5px 8px;margin:4px 0 2px;'
		+ 'border:1px dashed rgba(0,22,137,.35);border-radius:6px;background:rgba(0,22,137,.05);'
		+ 'text-decoration:none;font:700 11px/1.2 -apple-system,BlinkMacSystemFont,sans-serif;'
		+ 'color:#001689;text-transform:uppercase;letter-spacing:.3px;">'
		+ '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" '
		+ 'stroke-linecap="round" stroke-linejoin="round" style="flex:0 0 auto;"><path d="M12 11c0-1.1.9-2 2-2h4a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6a2 2 0 012-2h4m4 0V7a4 4 0 10-8 0v4"/></svg>'
		+ '<span>Please login for pricing</span>'
		+ '<span style="margin-left:auto;color:rgba(0,22,137,.7);">Sign in →</span>'
		+ '</a>';
}

function _resolveStock(part) {
	var sku = part.sku || '';
	// 1. Real-time storefront database stock (from /api/prices/stock) wins — matches PartCard's mysqlStock branch.
	if (sku && _stockOverrides[sku]) {
		var s = _stockOverrides[sku];
		return { qty: s.available, branch: '', status: s.status };
	}
	// 2. Fallback: branch-aware warehouse_stock from Solr.
	var branch = '';
	try { branch = (window.top || window).localStorage.getItem('SelectedBranchName') || ''; }
	catch (e) { try { branch = localStorage.getItem('SelectedBranchName') || ''; } catch (e2) {} }
	var ws = part.warehouse_stock;
	if (!ws || typeof ws !== 'object') return { qty: undefined, branch: branch, status: part.stock_status || 'out_of_stock' };
	var qty;
	if (branch) {
		var lb = branch.toLowerCase();
		for (var key in ws) { if (ws.hasOwnProperty(key) && key.toLowerCase() === lb) { qty = ws[key]; break; } }
	} else {
		qty = 0;
		for (var k in ws) { if (ws.hasOwnProperty(k)) qty += (ws[k] || 0); }
	}
	return { qty: qty, branch: branch, status: qty !== undefined ? (qty > 0 ? 'in_stock' : 'out_of_stock') : (part.stock_status || 'out_of_stock') };
}

// Customer-specific price wins over the Solr-indexed price — matches PartCard's overridePrice branch.
function _resolvePrice(part) {
	var sku = part.sku || '';
	if (sku && typeof _priceOverrides[sku] === 'number') return _priceOverrides[sku];
	var n = Number(part.price);
	return !n || isNaN(n) ? 0 : n;
}

// Promo "was" / reducedFrom (excl-VAT) for the given SKU, or 0 if not on promo.
function _resolveReducedFrom(part) {
	var sku = part.sku || '';
	if (sku && typeof _reducedFromOverrides[sku] === 'number') return _reducedFromOverrides[sku];
	return 0;
}

// Standard / B2C / retail price (excl-VAT), or 0 when not fetched.
function _resolveStandardPrice(part) {
	var sku = part.sku || '';
	if (sku && typeof _standardPriceOverrides[sku] === 'number') return _standardPriceOverrides[sku];
	return 0;
}

// Fetch /api/prices/customer + /api/prices/stock in parallel for the counter-part SKUs and
// populate _priceOverrides / _stockOverrides before re-rendering cards. Mirrors the
// useCustomerPrices + useStockStatus hooks invoked by ResultsGrid.
function fetchPricesAndStock(skus) {
	if (!skus || skus.length === 0) return Promise.resolve();
	var sessionRef = getSessionRef();
	var qs = '?skus=' + encodeURIComponent(skus.join(','));

	var pricesP = sessionRef
		? apiFetch('/api/prices/customer' + qs, { headers: { 'X-Session-Reference': sessionRef } })
			.then(function (r) { return r.ok ? r.json() : { prices: [] }; })
			.then(function (data) {
				var list = (data && data.prices) ? data.prices : [];
				for (var i = 0; i < list.length; i++) {
					var p = list[i];
					if (p && p.sku && typeof p.price === 'number') {
						_priceOverrides[p.sku] = p.price;
						if (typeof p.reducedFrom === 'number' && p.reducedFrom > p.price) {
							_reducedFromOverrides[p.sku] = p.reducedFrom;
						}
					}
				}
			})
			.catch(function () { /* prices are non-critical */ })
		: Promise.resolve(); // No session cookie → no customer override, fall back to Solr price.

	// Standard / B2C / retail comparison prices — only fetched when the role
	// has showPriceComparison enabled (typical for B2B).
	var standardP = (_features && _features.showPriceComparison === true)
		? apiFetch('/api/prices/standard' + qs)
			.then(function (r) { return r.ok ? r.json() : { prices: [] }; })
			.then(function (data) {
				var list = (data && data.prices) ? data.prices : [];
				for (var i = 0; i < list.length; i++) {
					var p = list[i];
					if (p && p.sku && typeof p.price === 'number') _standardPriceOverrides[p.sku] = p.price;
				}
			})
			.catch(function () { /* non-critical */ })
		: Promise.resolve();

	var stockP = apiFetch('/api/prices/stock' + qs)
		.then(function (r) { return r.ok ? r.json() : { stock: [] }; })
		.then(function (data) {
			var list = (data && data.stock) ? data.stock : [];
			for (var i = 0; i < list.length; i++) {
				var s = list[i];
				if (s && s.sku) _stockOverrides[s.sku] = { available: s.available, status: s.status };
			}
		})
		.catch(function () { /* stock is non-critical */ });

	return Promise.all([pricesP, stockP, standardP]);
}

function navigateToPart(partId, e, btn) {
	if (e) e.preventDefault();
	// Only swap in the spinner when the click came from the plain-text
	// Details button. Image and name links wrap styled DOM (a div with
	// background-image, an <h3>) — mutating their textContent destroys
	// that content and leaves the card blank after bfcache restore.
	if (btn && btn.classList && btn.classList.contains('pc-btn-details')) {
		btn.textContent = '';
		var sp = document.createElement('span');
		sp.style.cssText = 'display:inline-block;width:12px;height:12px;border:2px solid #bbb;border-top-color:#1e3a5f;border-radius:50%;animation:pcp-spin .7s linear infinite;vertical-align:middle;';
		btn.appendChild(sp);
		btn.style.pointerEvents = 'none';
	} else if (btn) {
		// No visual mutation for image/name links — just disable repeat clicks.
		btn.style.pointerEvents = 'none';
	}
	var base = getAppBaseUrl().replace(/\/$/, '');
	var fallbackUrl = inAppPartUrl(partId);
	apiFetch('/api/parts/' + encodeURIComponent(partId) + '/storefront-url')
		.then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
		.then(function (result) {
			var url = (result.data && result.data.url) ? result.data.url.trim() : '';
			window.location.href = url || fallbackUrl;
		})
		.catch(function () { window.location.href = fallbackUrl; });
}

function openQuickViewModal(partId) {
	var doc = getTopDocument();
	var body = getTopBody();
	var modalId = 'parts-finder-quick-view-modal';
	var modal = doc.getElementById(modalId);

	if (!modal) {
		modal = doc.createElement('div');
		modal.id = modalId;

		var drawer = doc.createElement('div');
		drawer.id = 'pcp-qv-drawer';

		// Close button row
		var closeRow = doc.createElement('div');
		closeRow.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-bottom:1px solid #e5e7eb;flex-shrink:0;background:#fff;';
		var closeTitle = doc.createElement('span');
		closeTitle.style.cssText = 'font-size:15px;font-weight:700;color:#111827;';
		closeTitle.textContent = 'Part Details';
		var closeBtn = doc.createElement('button');
		closeBtn.setAttribute('aria-label', 'Close');
		closeBtn.style.cssText = 'width:32px;height:32px;border-radius:50%;border:1px solid #e5e7eb;background:#f9fafb;color:#6b7280;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:18px;line-height:1;';
		closeBtn.innerHTML = '<svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>';

		function closeDrawer() {
			modal.classList.remove('pcp-qv-open');
			drawer.classList.remove('pcp-qv-drawer-open');
			drawer.style.transform = 'translateX(100%)';
			var ca = drawer.querySelector('.pcp-qv-body');
			if (ca) setTimeout(function () { ca.innerHTML = ''; }, 300);
		}

		closeBtn.addEventListener('click', closeDrawer);
		closeRow.appendChild(closeTitle);
		closeRow.appendChild(closeBtn);

		var contentArea = doc.createElement('div');
		contentArea.className = 'pcp-qv-body';

		drawer.appendChild(closeRow);
		drawer.appendChild(contentArea);
		modal.appendChild(drawer);

		modal.addEventListener('click', function (e) {
			if (e.target === modal) closeDrawer();
		});

		// Close on Escape
		doc.addEventListener('keydown', function (e) {
			if (e.key === 'Escape' && modal.classList.contains('pcp-qv-open')) closeDrawer();
		});

		body.appendChild(modal);
	}

	var contentArea = modal.querySelector('.pcp-qv-body');
	var drawer = doc.getElementById('pcp-qv-drawer');
	modal.classList.add('pcp-qv-open');
	drawer.classList.add('pcp-qv-drawer-open');
	drawer.style.transform = '';
	contentArea.innerHTML = '<div style="text-align:center;padding:60px 20px;"><div style="display:inline-block;width:48px;height:48px;border:4px solid #f3f3f3;border-top:4px solid #d9272e;border-radius:50%;animation:pcp-spin .8s linear infinite;"></div><p style="margin-top:20px;color:#6c757d;">Loading part details...</p></div>';

	apiFetch('/api/parts/' + encodeURIComponent(partId))
		.then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
		.then(function (part) { _renderQuickViewContent(contentArea, part); })
		.catch(function () { contentArea.innerHTML = '<div style="text-align:center;padding:60px;color:#dc2626;">Could not load part details.</div>'; });
}

function _renderQuickViewContent(container, part) {
	var images = (part.imageUrls && part.imageUrls.length > 0) ? part.imageUrls : (part.imageUrl ? [part.imageUrl] : []);
	var ws = part.warehouse_stock;
	var qty = ws ? Object.keys(ws).reduce(function (s, k) { return s + (ws[k] || 0); }, 0) : undefined;
	var stockStatus = qty !== undefined ? (qty > 0 ? 'in_stock' : 'out_of_stock') : (part.stock_status || 'out_of_stock');
	var isIn = stockStatus === 'in_stock';
	// showStockQuantity gates the numeric count; off → status-only.
	var stockText = (_features.showStockQuantity !== false && qty !== undefined && qty > 0)
		? (qty + ' In Stock')
		: (isIn ? 'In Stock' : 'Out Of Stock');
	// Customer-pricelist override (already populated when the cards rendered)
	// so the Quick-view price matches the card behind it.
	var qvPriceNum = _resolvePrice(part);
	var priceIncl = qvPriceNum > 0 ? (qvPriceNum * 1.15).toFixed(2) : null;
	var priceExcl = qvPriceNum > 0 ? qvPriceNum.toFixed(2) : null;
	var qvReducedFromExcl = _resolveReducedFrom(part);
	var qvIsPromo = qvPriceNum > 0 && qvReducedFromExcl > qvPriceNum;
	var qvReducedFromIncl = qvIsPromo ? (qvReducedFromExcl * 1.15).toFixed(2) : null;
	var qvStandardExcl = _resolveStandardPrice(part);
	var qvShowComparison = (_features.showPriceComparison === true) && qvStandardExcl > 0 && qvStandardExcl !== qvPriceNum;
	var qvStandardIncl = qvShowComparison ? (qvStandardExcl * 1.15).toFixed(2) : null;

	/* Single column stacked layout for the side drawer */
	var h = '<div style="display:flex;flex-direction:column;padding:20px;gap:20px;font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,sans-serif;">';

	/* Image */
	h += '<div style="background:#f9fafb;border-radius:8px;padding:16px;display:flex;align-items:center;justify-content:center;min-height:200px;">';
	if (images.length > 0) {
		h += '<img id="pcp-qv-main-img" src="' + images[0] + '" alt="' + esc(part.title || '') + '" style="max-width:100%;max-height:240px;object-fit:contain;" onerror="this.style.display=\'none\'">';
	} else {
		h += '<div style="color:#d1d5db;font-size:14px;">No image available</div>';
	}
	h += '</div>';

	/* Thumbnails */
	if (images.length > 1) {
		h += '<div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap;">';
		for (var i = 0; i < Math.min(images.length, 6); i++) {
			var border = i === 0 ? 'border:2px solid #d9272e;' : 'border:1px solid #e5e7eb;';
			h += '<button data-src="' + images[i] + '" style="width:48px;height:48px;border-radius:6px;overflow:hidden;cursor:pointer;' + border + 'padding:0;background:none;" class="pcp-qv-thumb">';
			h += '<img src="' + images[i] + '" style="width:100%;height:100%;object-fit:cover;">';
			h += '</button>';
		}
		h += '</div>';
	}

	/* Title */
	h += '<h2 style="font-size:17px;font-weight:700;color:#111827;margin:0;line-height:1.4;">' + esc(part.title || '') + '</h2>';

	/* Meta row: SKU / Brand / Category */
	h += '<div style="display:flex;flex-direction:column;gap:6px;">';
	h += '<div style="display:flex;align-items:center;gap:8px;"><span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;min-width:64px;">SKU</span><span style="font-size:13px;font-weight:700;font-family:monospace;color:#111827;">' + esc(part.sku || 'N/A') + '</span></div>';
	if (part.brand) h += '<div style="display:flex;align-items:center;gap:8px;"><span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;min-width:64px;">Brand</span><span style="font-size:13px;font-weight:600;color:#111827;">' + esc(part.brand) + '</span></div>';
	if (part.category) h += '<div style="display:flex;align-items:center;gap:8px;"><span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;min-width:64px;">Category</span><span style="font-size:13px;font-weight:600;color:#111827;">' + esc(part.category) + '</span></div>';
	h += '</div>';

	/* Price + stock — gated per-feature; whole panel collapses if every flag is off */
	if (_features.showPrices !== false || _features.showStockStatus !== false || (_features.showPrices === false && isAnonymous())) {
		h += '<div style="background:#f9fafb;border-radius:8px;padding:16px;border:1px solid #e5e7eb;">';
		if (_features.showPrices === false && isAnonymous()) {
			h += '<div style="margin-bottom:10px;">' + _loginPromptHtml() + '</div>';
		}
		if (_features.showPrices !== false) {
			if (priceIncl && qvStandardIncl) {
				/* Price comparison: normal block with RETAIL amounts, small grey workshop line below. */
				var qvLoc = function (n) { return 'R' + parseFloat(n).toLocaleString('en-ZA', {minimumFractionDigits:2,maximumFractionDigits:2}); };
				var qvCmpIncl = _features.showPriceInclVat !== false;
				var qvCmpExcl = _features.showPriceExclVat !== false;
				if (qvCmpIncl) {
					h += '<div style="font-size:26px;font-weight:800;color:#2c347c;line-height:1.2;display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;">' + qvLoc(qvStandardIncl) + '<span style="font-size:12px;font-weight:400;color:#6b7280;">incl. VAT</span></div>';
				}
				if (qvCmpExcl) {
					h += '<div style="font-size:12px;color:#6b7280;margin-top:2px;">' + qvLoc(qvStandardExcl.toFixed(2)) + ' excl. VAT</div>';
				}
				h += workshopLineHtml({ incl: qvCmpIncl ? qvLoc(priceIncl) : null, excl: qvCmpExcl ? qvLoc(priceExcl) : null, wasIncl: qvReducedFromIncl ? qvLoc(qvReducedFromIncl) : null, isPromo: qvIsPromo, style: 'font-size:11px;color:#6b7280;margin-top:4px;', taxStyle: 'color:#9ca3af;' });
			} else if (priceIncl) {
				if (_features.showPriceInclVat !== false) {
					h += '<div style="font-size:26px;font-weight:800;color:#2c347c;line-height:1.2;display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;">R' + parseFloat(priceIncl).toLocaleString('en-ZA', {minimumFractionDigits:2,maximumFractionDigits:2}) + '<span style="font-size:12px;font-weight:400;color:#6b7280;">incl. VAT</span>';
					if (qvReducedFromIncl) {
						h += '<span style="font-size:12px;color:#9ca3af;font-weight:400;" title="Regular price">R' + parseFloat(qvReducedFromIncl).toLocaleString('en-ZA', {minimumFractionDigits:2,maximumFractionDigits:2}) + '</span>';
					}
					if (qvIsPromo) {
						h += '<span style="padding:1px 6px;border-radius:9999px;background:#fee2e2;color:#b91c1c;font-size:10px;font-weight:700;letter-spacing:.3px;">ON PROMO</span>';
					}
					h += '</div>';
				}
				if (_features.showPriceExclVat !== false) {
					h += '<div style="font-size:12px;color:#6b7280;margin-top:2px;">R' + parseFloat(priceExcl).toLocaleString('en-ZA', {minimumFractionDigits:2,maximumFractionDigits:2}) + ' excl. VAT</div>';
				}
			} else {
				h += '<div style="font-size:16px;font-weight:600;color:#4b5563;">Price on request</div>';
			}
		}
		if (_features.showStockStatus !== false) {
			h += '<span style="display:inline-block;margin-top:8px;padding:3px 10px;border-radius:9999px;font-size:11px;font-weight:700;background:' + (isIn ? '#d1fae5' : '#fee2e2') + ';color:' + (isIn ? '#065f46' : '#991b1b') + ';">' + esc(stockText) + '</span>';
		}
		h += '</div>';
	}

	/* OEM Codes — gated on _features.showOemCodes */
	if (_features.showOemCodes !== false && part.oem_codes && part.oem_codes.length > 0) {
		h += '<div><h3 style="font-size:13px;font-weight:700;color:#111827;margin:0 0 8px;">OEM Codes</h3>';
		h += '<div style="display:flex;flex-direction:column;gap:4px;">';
		for (var oi = 0; oi < part.oem_codes.length; oi++) {
			var oemRaw = part.oem_codes[oi];
			var colonIdx = oemRaw.indexOf(':');
			var oemMfr = colonIdx > -1 ? oemRaw.substring(0, colonIdx).trim() : '';
			var oemVal = colonIdx > -1 ? oemRaw.substring(colonIdx + 1).trim() : oemRaw;
			if (oemMfr) {
				h += '<div style="display:flex;align-items:center;gap:8px;padding:4px 10px;background:#f3f4f6;border-radius:6px;">';
				h += '<span style="font-size:11px;font-weight:700;color:#6b7280;min-width:80px;">' + esc(oemMfr) + '</span>';
				h += '<span style="font-size:12px;font-weight:600;color:#1f2937;font-family:monospace;">' + esc(oemVal) + '</span>';
				h += '</div>';
			} else {
				h += '<span style="display:inline-block;background:#f3f4f6;color:#1f2937;font-size:12px;font-weight:500;padding:3px 10px;border-radius:6px;font-family:monospace;">' + esc(oemVal) + '</span>';
			}
		}
		h += '</div></div>';
	}

	/* Attributes */
	if (part.attributes && part.attributes.length > 0) {
		h += '<div><h3 style="font-size:13px;font-weight:700;color:#111827;margin:0 0 8px;">Attributes</h3><div style="display:flex;flex-direction:column;gap:6px;">';
		for (var ai = 0; ai < part.attributes.length; ai++) {
			var attr = part.attributes[ai];
			var ci = attr.indexOf(':');
			var aKey = ci > -1 ? attr.substring(0, ci) : attr;
			var aVal = ci > -1 ? attr.substring(ci + 1).trim() : '';
			h += '<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:8px 12px;background:#f9fafb;border-radius:6px;font-size:13px;border:1px solid #f3f4f6;"><span style="font-weight:600;color:#6b7280;">' + esc(aKey) + '</span><span style="font-weight:700;color:#111827;text-align:right;word-break:break-word;">' + esc(aVal) + '</span></div>';
		}
		h += '</div></div>';
	}

	/* Vehicle Compatibility — gated on _features.showCompatibility */
	if (_features.showCompatibility !== false && part.fitments && part.fitments.length > 0) {
		h += '<div><div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;"><h3 style="font-size:13px;font-weight:700;color:#111827;margin:0;">Vehicle Compatibility</h3><span style="font-size:12px;color:#6b7280;">' + part.fitments.length + ' vehicle' + (part.fitments.length !== 1 ? 's' : '') + '</span></div>';
		h += '<div style="display:flex;flex-direction:column;gap:6px;">';
		for (var fi = 0; fi < part.fitments.length; fi++) {
			var fit = part.fitments[fi];
			var yr = fit.fit_year_from && fit.fit_year_to ? (fit.fit_year_from === fit.fit_year_to ? fit.fit_year_from : fit.fit_year_from + ' - ' + fit.fit_year_to) : (fit.fit_year_from ? fit.fit_year_from + '+' : (fit.fit_year_to ? 'Up to ' + fit.fit_year_to : ''));
			h += '<div style="display:flex;flex-direction:column;padding:8px 12px;border-radius:6px;background:#f9fafb;font-size:12px;">';
			h += '<div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:4px;">';
			if (fit.fit_make) h += '<span style="background:#2c347c;color:#fff;font-weight:700;padding:2px 8px;border-radius:4px;">' + esc(fit.fit_make) + '</span>';
			if (fit.fit_model) h += '<span style="background:#374151;color:#fff;font-weight:600;padding:2px 8px;border-radius:4px;">' + esc(fit.fit_model) + '</span>';
			if (fit.fit_submodel) h += '<span style="background:#6b7280;color:#fff;font-weight:500;padding:2px 8px;border-radius:4px;">' + esc(fit.fit_submodel) + '</span>';
			h += '</div><div style="display:flex;gap:12px;flex-wrap:wrap;">';
			if (yr) h += '<span style="color:#4b5563;font-weight:500;">' + esc(yr) + '</span>';
			if (fit.fit_engine) h += '<span style="color:#6b7280;">' + esc(fit.fit_engine) + '</span>';
			if (fit.fit_notes) h += '<span style="color:#9ca3af;font-style:italic;width:100%;margin-top:2px;">' + esc(fit.fit_notes) + '</span>';
			h += '</div></div>';
		}
		h += '</div></div>';
	}

	h += '</div>'; /* end column */
	container.innerHTML = h;

	/* Wire thumbnail clicks */
	container.querySelectorAll('.pcp-qv-thumb').forEach(function (btn) {
		btn.addEventListener('click', function () {
			var src = btn.getAttribute('data-src');
			var mainImg = container.querySelector('#pcp-qv-main-img');
			if (mainImg) mainImg.src = src;
			container.querySelectorAll('.pcp-qv-thumb').forEach(function (b) {
				b.style.border = b === btn ? '2px solid #d9272e' : '1px solid #e5e7eb';
			});
		});
	});
}

function buildCardHtml(part) {
	var id = part.id || part.sku || '';
	var sku = part.sku || '';
	var name = part.title || sku || 'Product';
	var brand = part.brand || '';
	var brandImg = part.manufacturerImage || '';
	var _stk = _resolveStock(part);
	var stockStatus = _stk.status;
	var isInStock = stockStatus === 'in_stock';
	var isBackorder = stockStatus === 'backorder';
	// Availability copy — status-only, PLUS the numeric count when
	// showStockQuantity is on (in-stock with a positive qty).
	var avail = (_features.showStockQuantity !== false && isInStock && typeof _stk.qty === 'number' && _stk.qty > 0)
		? (_stk.qty + ' In Stock')
		: (isInStock ? 'In Stock' : isBackorder ? 'Backorder' : 'Out of Stock');
	var availDot = isInStock ? 'in' : isBackorder ? 'backorder' : 'out';
	// Use customer-pricelist override when present, falling back to the Solr-indexed price.
	var priceNum = _resolvePrice(part);
	var priceIncl = formatPriceIncl(priceNum);
	var priceExcl = formatPriceExcl(priceNum);
	var reducedFromExcl = _resolveReducedFrom(part);
	var isPromo = priceNum > 0 && reducedFromExcl > priceNum;
	var reducedFromIncl = isPromo ? formatPriceIncl(reducedFromExcl) : null;
	var standardExcl = _resolveStandardPrice(part);
	var showComparison = (_features.showPriceComparison === true) && standardExcl > 0 && standardExcl !== priceNum;
	var standardIncl = showComparison ? formatPriceIncl(standardExcl) : null;
	// Presentation Mode tags (see shared/presentation.js): swap trade↔retail text.
	var pfRetailIncl = standardExcl > 0 ? formatPriceIncl(standardExcl) : '';
	var pfRetailExcl = standardExcl > 0 ? formatPriceExcl(standardExcl) : '';
	var pfInclAttr = pfRetailIncl ? (' data-pf-price-trade="' + esc(priceIncl) + '" data-pf-price-retail="' + esc(pfRetailIncl) + '"') : '';
	var pfExclAttr = pfRetailExcl ? (' data-pf-price-trade="' + esc(priceExcl) + '" data-pf-price-retail="' + esc(pfRetailExcl) + '"') : '';
	var imgUrl = part.imageUrl ? (part.imageUrl + '?height=262&width=262') : '';

	var h = '';
	h += '<article class="pc-card" data-mh="product" data-id="' + esc(id) + '" data-name="' + esc(name) + '" data-stock="' + (isInStock ? 'true' : 'false') + '">';

	/* Image */
	h += '<a href="#" class="pc-img-link pcp-nav-link" data-part-id="' + esc(id) + '">';
	h += '<div class="pc-img-wrap">';
	h += '<div class="pc-img" role="img" aria-label="' + esc(name || sku) + '" style="background-image:' + (imgUrl ? 'url(\'' + esc(imgUrl) + '\')' : 'none') + ';"></div>';
	h += '</div>';
	h += '</a>';

	/* Body */
	h += '<div class="pc-body">';
	/* Brand */
	if (brand) {
		h += '<div class="pc-brand">';
		if (brandImg) {
			h += '<img src="' + esc(brandImg) + '" alt="' + esc(brand) + '" loading="lazy" style="max-height:22px;max-width:80px;object-fit:contain;">';
		} else {
			h += '<span class="pc-brand-text">' + esc(brand) + '</span>';
		}
		h += '</div>';
	}
	/* Name */
	h += '<a href="#" class="pc-name-link pcp-nav-link" data-part-id="' + esc(id) + '"><h3 class="pc-name">' + esc(name) + '</h3></a>';
	/* SKU */
	if (sku) h += '<p class="pc-sku">Item code: ' + esc(sku) + '</p>';
	/* Anonymous visitor + prices hidden → render the login prompt instead. */
	if (_features.showPrices === false && isAnonymous()) {
		h += _loginPromptHtml();
	}

	/* Price — gated on _features.showPrices (master) + incl/excl sub-toggles.
	   Promo: strikethrough "was" + ON PROMO badge. Retail comparison line
	   shown to roles with showPriceComparison (typical for B2B). */
	if (_features.showPrices !== false && standardIncl && priceNum > 0) {
		/* Price comparison: the normal price block with the RETAIL amounts, and
		   the customer's own price on a small grey "Workshop:" line below it. */
		var cmpIncl = _features.showPriceInclVat !== false;
		var cmpExcl = _features.showPriceExclVat !== false;
		h += '<div class="pc-price-row" style="flex-wrap:wrap;">';
		if (cmpIncl) h += '<span class="pc-price">' + esc(standardIncl) + '</span><span class="pc-price-tax">incl. VAT</span>';
		h += '</div>';
		if (cmpExcl) h += '<div class="pc-price-excl"><span>' + esc(pfRetailExcl) + '</span> excl. VAT</div>';
		h += workshopLineHtml({ incl: cmpIncl ? priceIncl : null, excl: cmpExcl ? priceExcl : null, wasIncl: reducedFromIncl, isPromo: isPromo });
	} else if (_features.showPrices !== false) {
		/* Retail first, the customer's own (workshop) price below. */
		if (standardIncl) {
			h += '<div class="pf-retail-line" style="font-size:11px;color:#6b7280;margin-bottom:2px;">Retail: <span style="color:#9ca3af">' + esc(standardIncl) + '</span> <span style="color:#9ca3af;font-size:10px;">incl. VAT</span></div>';
		}
		h += '<div class="pc-price-row" style="flex-wrap:wrap;">';
		if (priceIncl && _features.showPriceInclVat !== false) {
			h += '<span class="pc-price"' + pfInclAttr + '>' + esc(priceIncl) + '</span><span class="pc-price-tax">incl. VAT</span>';
			if (reducedFromIncl) {
				h += '<span class="pf-retail-line" style="margin-left:6px;color:#9ca3af;font-size:11px;" title="Regular price">' + esc(reducedFromIncl) + '</span>';
			}
			if (isPromo) {
				h += '<span class="pf-retail-line" style="margin-left:6px;padding:1px 6px;border-radius:9999px;background:#fee2e2;color:#b91c1c;font-size:10px;font-weight:700;letter-spacing:.3px;">ON PROMO</span>';
			}
		} else if (!priceIncl) {
			h += '<span class="pc-price">Price on request</span>';
		}
		h += '</div>';
		if (priceExcl && _features.showPriceExclVat !== false) {
			h += '<div class="pc-price-excl"><span' + pfExclAttr + '>' + esc(priceExcl) + '</span> excl. VAT</div>';
		}
	}
	/* Availability — gated on _features.showStockStatus */
	if (_features.showStockStatus !== false) {
		h += '<div class="pc-availability"><span class="pc-avail-dot ' + availDot + '" aria-hidden="true"></span><span class="pc-avail-label">' + esc(avail) + '</span></div>';
	}
	h += '</div>'; /* /pc-body */

	/* Actions — Quick View / Details / Add to Cart each gated */
	h += '<div class="pc-actions">';
	h += '<div class="pc-btn-row">';
	if (_features.allowQuickView !== false) {
		h += '<button type="button" class="btn btn-outline pc-btn-quickview pcp-qv-btn-card" data-part-id="' + esc(id) + '" aria-label="Quick view ' + esc(name) + '" title="Quick View">';
		h += '<span class="material-symbols-outlined">visibility</span>';
		h += '</button>';
	}
	h += '<a href="#" class="btn btn-outline pc-btn-details pcp-nav-link" data-part-id="' + esc(id) + '" aria-label="View details for ' + esc(name) + '" title="View product details">Details</a>';
	if (_features.allowAddToCart !== false) {
		h += '<button type="button" class="btn btn-primary pc-btn-cart quick-add pcp-cart-btn" data-sku="' + esc(sku) + '" data-name="' + esc(name) + '" aria-label="Add ' + esc(name) + ' to cart" title="Add to cart">';
		h += '<span class="material-symbols-outlined">add_shopping_cart</span>';
		h += '</button>';
	}
	h += '</div>';
	h += '</div>'; /* /pc-actions */

	h += '</article>';
	return h;
}

function addToCart(sku, name, btn) {
	if (!sku) return;
	// Credit gate: block on-account purchase when the visitor has no credit
	// at the active branch (shared PFCreditGuard). Degrades to proceeding
	// when the guard script isn't present.
	if (window.PFCreditGuard && window.PFCreditGuard.guard) {
		window.PFCreditGuard.guard({ sku: sku, qty: 1 }).then(function (ok) { if (ok) _addToCartNow(sku, name, btn); });
		return;
	}
	_addToCartNow(sku, name, btn);
}
function _addToCartNow(sku, name, btn) {
	if (!sku) return;
	btn.disabled = true;
	btn.innerHTML = '<span style="display:inline-block;width:14px;height:14px;border:2px solid rgba(255,255,255,.5);border-top-color:#fff;border-radius:50%;animation:pcp-spin .7s linear infinite;vertical-align:middle;"></span>';

	// Try storefront cart API first (Storefront8 / BigCommerce style). This is a
	// RELATIVE path on the storefront origin — NOT the parts-finder app base — so
	// it uses a raw fetch rather than apiFetch.
	var sfCartUrl = '/api/storefront/cart';
	fetch(sfCartUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ lineItems: [{ quantity: 1, sku: sku }] })
	})
	.then(function(r) {
		if (r.ok) {
			btn.disabled = false;
			btn.innerHTML = '<span class="material-symbols-outlined">check</span>';
			setTimeout(function() { btn.innerHTML = '<span class="material-symbols-outlined">add_shopping_cart</span>'; btn.disabled = false; }, 2000);
			var win = window;
			try { if (window.top) win = window.top; } catch(e) {}
			if (win.Cart && win.Cart.MiniCart && win.Cart.MiniCart.GetMiniCart) win.Cart.MiniCart.GetMiniCart();
			if (win.Cart && win.Cart.UpdateCartCount) win.Cart.UpdateCartCount();
			if (win.Utils && typeof win.Utils.Toastr === 'function') win.Utils.Toastr(addedToCartToast(name, 1));
		} else { throw new Error('sf cart ' + r.status); }
	})
	.catch(function() {
		// Fallback: Parts Finder cart API (app base + Bearer via apiFetch).
		apiFetch('/api/cart/add', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ sku: sku, quantity: 1 })
		})
		.then(function(r2) {
			btn.disabled = false;
			btn.innerHTML = r2.ok
				? '<span class="material-symbols-outlined">check</span>'
				: '<span class="material-symbols-outlined">add_shopping_cart</span>';
			if (r2.ok) setTimeout(function() { btn.innerHTML = '<span class="material-symbols-outlined">add_shopping_cart</span>'; }, 2000);
		})
		.catch(function() {
			btn.disabled = false;
			btn.innerHTML = '<span class="material-symbols-outlined">add_shopping_cart</span>';
		});
	});
}

function render(container, sku) {
	container.innerHTML = '<div class="pcp-wrap"><div class="pcp-loading">Loading counter parts...</div></div>';

	var limit = container.getAttribute('data-limit') ? parseInt(container.getAttribute('data-limit'), 10) : 6;
	if (isNaN(limit) || limit < 1) limit = 6;

	apiFetch('/api/parts/' + encodeURIComponent(sku) + '/counterparts?limit=' + limit)
		.then(function (res) {
			if (!res.ok) throw new Error('Counterparts API ' + res.status);
			return res.json();
		})
		.then(function (data) {
			var list = (data && data.counterparts) ? data.counterparts : [];
			var wrap = container.querySelector('.pcp-wrap');
			if (!wrap) return null;

			if (list.length === 0) {
				wrap.innerHTML = '<p class="pcp-section-title">Counter Parts</p><div class="pcp-empty">No counter parts found for this part.</div>';
				return null;
			}

			// Load role flags FIRST so fetchPricesAndStock can decide whether to
			// also request /api/prices/standard for the B2C comparison line.
			var skus = list.map(function (p) { return p && p.sku; }).filter(Boolean);
			return loadFeatures()
				.then(function (f) { _features = f || {}; return fetchPricesAndStock(skus); })
				.then(function () { return { list: list, wrap: wrap }; });
		})
		.then(function (ctx) {
			if (!ctx) return;
			var list = ctx.list;
			var wrap = ctx.wrap;

			var title = container.getAttribute('data-title') || 'Counter Parts';
			var html = '<p class="pcp-section-title">' + esc(title) + '</p><div class="pcp-grid">';
			list.forEach(function (part) {
				html += buildCardHtml(part);
			});
			html += '</div>';
			wrap.innerHTML = html;

			/* Navigate on image / name / details click */
			wrap.querySelectorAll('.pcp-nav-link').forEach(function (el) {
				el.addEventListener('click', function (e) {
					e.preventDefault();
					navigateToPart(el.getAttribute('data-part-id'), e, el);
				});
			});

			/* Quick View (drawer) buttons */
			wrap.querySelectorAll('.pcp-qv-btn-card').forEach(function (btn) {
				btn.addEventListener('click', function (e) {
					e.preventDefault();
					e.stopPropagation();
					openQuickViewModal(btn.getAttribute('data-part-id'));
				});
			});

			/* Add to Cart buttons */
			wrap.querySelectorAll('.pcp-cart-btn').forEach(function (btn) {
				btn.addEventListener('click', function (e) {
					e.preventDefault();
					addToCart(btn.getAttribute('data-sku'), btn.getAttribute('data-name') || '', btn);
				});
			});

		})
		.catch(function (err) {
			var wrap = container.querySelector('.pcp-wrap');
			if (wrap) wrap.innerHTML = '';
			console.warn('PartsCounterParts: fetch failed', err);
		});
}

// Graft a "Counter Parts" tab into the storefront's existing product tab strip
// when no explicit container is present. Returns the panel element, or null if
// the tab chrome isn't on the page.
function ensureTabContainer() {
	var topDoc = getTopDocument();
	var navTabsContain = topDoc.querySelector('.nav-tabs-contain');
	if (!navTabsContain) return null;
	var tabId = 'pcp-counterparts';
	var existing = topDoc.getElementById(tabId);
	if (existing) return existing;
	var tabList = navTabsContain.querySelector('.nav.nav-tabs');
	var tabContent = navTabsContain.querySelector('.tab-content');
	if (!tabList || !tabContent) return null;
	var li = topDoc.createElement('li');
	li.setAttribute('role', 'presentation');
	li.innerHTML = '<a href="#' + tabId + '" role="tab" data-toggle="tab">Counter Parts</a>';
	tabList.appendChild(li);
	var panel = topDoc.createElement('div');
	panel.setAttribute('role', 'tabpanel');
	panel.className = 'tab-pane';
	panel.id = tabId;
	tabContent.appendChild(panel);
	return panel;
}

// Resolve the current product SKU: the container's own data-sku wins, then the
// container's data-sku-selector (default #product-sku), then any [data-sku].
function resolveSku(container) {
	var direct = container && container.getAttribute('data-sku');
	if (direct && direct.trim()) return direct.trim();
	var selector = (container && container.getAttribute('data-sku-selector')) || '#product-sku';
	return getSku(selector);
}

export function init() {
	var topDoc = getTopDocument();

	// Explicit target (if the page dropped one in). Resolved before touching the
	// tab chrome so a SKU-less page can no-op without side effects.
	var container = topDoc.getElementById('parts-counter-parts') ||
		topDoc.querySelector('[data-parts-counter-parts]');

	var sku = resolveSku(container);
	if (!sku) {
		// No SKU → not a product page (or nothing to show). No-op cleanly.
		if (container) console.warn('PartsCounterParts: No SKU found. Add data-sku on the container, an #product-sku input, or set data-sku-selector.');
		return;
	}

	// No explicit container but we do have a product SKU → graft a tab into the
	// storefront's product tab strip. If that chrome isn't present either, no-op.
	if (!container) container = ensureTabContainer();
	if (!container) return;

	_container = container;
	render(container, sku);
}

// Reset button states when page is restored from bfcache (browser back/forward).
// Critical: only the .pc-btn-details text button should have its textContent
// rewritten. Image links contain a styled <div class="pc-img"> with a
// background-image; name links contain an <h3>. Wiping their textContent
// here was destroying the product images on bfcache restore.
window.addEventListener('pageshow', function (e) {
	if (e.persisted && _container) {
		_container.querySelectorAll('.pcp-cart-btn').forEach(function (btn) {
			btn.disabled = false;
			btn.innerHTML = '<span class="material-symbols-outlined">add_shopping_cart</span>';
		});
		_container.querySelectorAll('.pc-btn-details.pcp-nav-link').forEach(function (btn) {
			btn.textContent = 'Details';
			btn.style.pointerEvents = '';
		});
		// Image + name links: restore pointer events only — leave inner DOM intact.
		_container.querySelectorAll('.pc-img-link.pcp-nav-link, .pc-name-link.pcp-nav-link').forEach(function (link) {
			link.style.pointerEvents = '';
		});
	}
});

export var id = 'counter-parts';
